import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    SK_CHAT_DETAIL_CONTAINER_CLASS,
    DEFAULT_ROWS_PER_PAGE,
    UNAUTHORIZED_REDIRECT_DESCRIPTION,
    UNAUTHORIZED_STATUS_CODE,
    GENERIC_ACTION_ERROR_TITLE,
    GENERIC_ACTION_ERROR_DESCRIPTION
} from "../../../constants/sidekick";
import { ChatDetailHeader } from "../../../components/sidekick/sessionDetail/ChatDetailHeader";
import { ChatDetailTable } from "../../../components/sidekick/sessionDetail/ChatDetailTable";
import { useChatSessionDetails } from "../../../hooks/useChatSessionDetails";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearChatDetail, selectChatDetail } from "../../../features/sidekick/sidekickSlice";
import { Snackbar, SnackbarMessage, Typography } from "@panviva/panviva-react-ui";
import "./ChatDetailContainer.scss";
import { forceLogoutToLogin } from "../../../utils/auth/forceLogout";


interface ChatDetailParams {
    conversationId: string;
}

const ChatDetailContainer: FunctionComponent = () => {
    // Get conversation ID from URL params
    const { conversationId } = useParams<ChatDetailParams>();
    const dispatch = useDispatch();

    // Get the chat detail state from Redux
    const chatDetailState = useSelector(selectChatDetail);

    const [showActionErrorToast, setShowActionErrorToast] = useState(false);
    const [actionToastTrigger, setActionToastTrigger] = useState(0);

    const openActionErrorToast = () => {
        setShowActionErrorToast(true);
        setActionToastTrigger((prev) => prev + 1);
    };

    // Use the Redux chat session details hook with initial pagination settings
    // TODO: Pagination implementation for session details needs to be confirmed from product team
    const chatDetailProps = useChatSessionDetails(conversationId, 1, DEFAULT_ROWS_PER_PAGE);

    // Derived entirely from Redux state (error, data, lastRequestSource) — no local state involved.
    // This ensures controls automatically re-enable when a subsequent fetch succeeds and Redux
    // clears the error, preventing stale disabled UI after error recovery.
    const isInitialLoadFailure = useMemo(() => {
        return Boolean(
            chatDetailState.error &&
            chatDetailState.data.length === 0 &&
            chatDetailState.lastRequestSource === 'initial'
        );
    }, [chatDetailState.error, chatDetailState.data.length, chatDetailState.lastRequestSource]);

    const isUnauthorizedInitialLoadFailure =
        isInitialLoadFailure && chatDetailState.errorStatusCode === UNAUTHORIZED_STATUS_CODE;

    const isUnauthorizedActionFailure = Boolean(
        chatDetailState.errorStatusCode === UNAUTHORIZED_STATUS_CODE &&
        chatDetailState.error &&
        chatDetailState.lastRequestSource === 'pagination'
    );

    useEffect(() => {
        const isPaginationFailure = Boolean(
            chatDetailState.error &&
            chatDetailState.lastRequestSource === 'pagination'
        );
        if (isPaginationFailure) {
            openActionErrorToast();
        }
    }, [chatDetailState.error, chatDetailState.lastRequestSource]);

    useEffect(() => {
        if (!showActionErrorToast) return;
        // Note: each new error resets this timer via actionToastTrigger, so the toast stays
        // visible for 4 seconds from the most recent error. showActionErrorToast remains true
        // throughout rapid successive failures so there is no visual flicker.
        const timeoutId = window.setTimeout(() => {
            setShowActionErrorToast(false);
        }, 4000);
        return () => window.clearTimeout(timeoutId);
    }, [showActionErrorToast, actionToastTrigger]);

    useEffect(() => {
        if (!isUnauthorizedInitialLoadFailure && !isUnauthorizedActionFailure) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            forceLogoutToLogin();
        }, 4000);

        return () => window.clearTimeout(timeoutId);
    }, [isUnauthorizedInitialLoadFailure, isUnauthorizedActionFailure, actionToastTrigger]);

    // Store the conversation subject in sessionStorage when it's available
    // This ensures it's preserved during browser navigation (forward/back buttons)
    useEffect(() => {
        if (chatDetailProps.conversationSubject && conversationId) {
            sessionStorage.setItem(`chat_subject_${conversationId}`, chatDetailProps.conversationSubject);
        }
    }, [chatDetailProps.conversationSubject, conversationId]);

    // Clear chat detail state when component unmounts
    // This ensures that when navigating back to the history view and then clicking on a row again,
    // we'll always fetch fresh data
    useEffect(() => {
        return () => {
            dispatch(clearChatDetail());
        };
    }, [dispatch]);

    // Try to get the subject from: Redux state, hook data, sessionStorage, or fallback to default
    const storedSubject = conversationId ? sessionStorage.getItem(`chat_subject_${conversationId}`) : null;
    const title = chatDetailState.conversationSubject ||
        chatDetailProps.conversationSubject ||
        storedSubject ||
        "Session Chat Title";

    return (
        <div className={SK_CHAT_DETAIL_CONTAINER_CLASS} role="main" aria-labelledby="chat-detail-title">
            <ChatDetailHeader title={title} />
            <ChatDetailTable
                {...chatDetailProps}
                isLoading={chatDetailProps.isLoading}
                isInitialLoadFailure={isInitialLoadFailure}
                isUnauthorizedInitialLoadFailure={isUnauthorizedInitialLoadFailure}
            />

            {/* Show Snackbar for any pagination action error and unauthorized action failure */}
            {(showActionErrorToast || isUnauthorizedActionFailure) && (
                // Wrapper div with ARIA attributes to ensure screen reader announcements
                // This is a workaround since @panviva/panviva-react-ui Snackbar doesn't support ARIA props directly
                <div role="alert" aria-live="assertive" aria-atomic="true">
                    <Snackbar className={'chat-detail-error-container'} status="error" icon='alert'>
                        <SnackbarMessage className='error-content'>
                            <Typography variant="body_2" className='error-text'>
                                {isUnauthorizedActionFailure
                                    ? UNAUTHORIZED_REDIRECT_DESCRIPTION
                                    : (GENERIC_ACTION_ERROR_TITLE + " " + GENERIC_ACTION_ERROR_DESCRIPTION)}
                            </Typography>
                        </SnackbarMessage>
                    </Snackbar>
                </div>
            )}
        </div>
    );
};

export default ChatDetailContainer;